/* eslint-disable no-undef */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useState, useEffect } from 'react';
import { Typography, Button } from '@mui/material';
import PaymentsAPI from 'api/PaymentsAPI';
import UsersAPI from 'api/UsersAPI';

interface IEditAccess {
  curTeam: any;
}

const EditAccess: React.FC<IEditAccess> = ({ curTeam }) => {
  const [ stripeUser, setStripeUser ] = useState({ isLoading: true, data: [] });
  const [ customerInfo, setCustomerInfo ] = useState<any>({ isLoading: true, data: {} });
  const [ adminUser, setAdminUser ] = useState<any>({ isLoading: true, data: {} });
  const [ selectedPlan, setSelectedPlan ] = useState('');
  const [ formValues, setFormValues ] = useState({
    freeTrialDays: '1',
    quantity: '1'
  });
  const proMonthlyPrice = process.env.REACT_APP_BITE_PRO_MONTHLY!;
  const standardMonthlyPrice = process.env.REACT_APP_BITE_STANDARD_MONTHLY!;
  const entMonthlyPrice = process.env.REACT_APP_BITE_ENT_MONTHLY!;

  const getStripeCustomer = async(teamAdmin: string) => {
    const stripeRes = await PaymentsAPI.getCustomerByEmail(teamAdmin);
    if (stripeRes.success) {
      setStripeUser({ isLoading: false, data: stripeRes.result.data });
    }
  };

  const getMatchedCusId = () => {
    stripeUser.data?.map((cus: any) => {
      if (cus.name === curTeam.name) {
        setCustomerInfo({ isLoading: false, data: cus });
      }
    });
  };

  const getAdminUser = async() => {
    const res = await UsersAPI.searchUsers(curTeam?.attributes?.team_admin[0]);
    if (res?.success) {
      setAdminUser({ isLoading: false, data: res.result[0] });
    }
  };

  const createCheckoutSession = async(tier: string) => {
    const standardObj = {
      lineItems: [
        {
          price: standardMonthlyPrice,
          adjustable_quantity: {
            enabled: false,
          },
          quantity: Number(formValues.quantity)
        }
      ],
      freeTrial: true,
      freeTrialDays: Number(formValues.freeTrialDays),
      team_id: curTeam.id,
      team_admin: curTeam.attributes.team_admin[0],
      team_name: curTeam.name,
      keycloakUserId: adminUser.data.id,
      moduleName: 'BITE Standard',
    };
    const proObj = {
      lineItems: [
        {
          price: proMonthlyPrice,
          adjustable_quantity: {
            enabled: false,
          },
          quantity: Number(formValues.quantity)
        }
      ],
      freeTrial: true,
      freeTrialDays: Number(formValues.freeTrialDays),
      team_id: curTeam.id,
      team_admin: curTeam.attributes.team_admin[0],
      team_name: curTeam.name,
      keycloakUserId: adminUser.data.id,
      moduleName: 'BITE Pro',
    };
    const entObj = {
      lineItems: [
        {
          price: entMonthlyPrice,
          adjustable_quantity: {
            enabled: false,
          },
          quantity: Number(formValues.quantity)
        }
      ],
      freeTrial: true,
      freeTrialDays: Number(formValues.freeTrialDays),
      team_id: curTeam.id,
      team_admin: curTeam.attributes.team_admin[0],
      team_name: curTeam.name,
      keycloakUserId: adminUser.data.id,
      moduleName: 'BITE Enterprise',
    };
    if (tier === 'standard') {
      const res = await PaymentsAPI.createCheckoutSession(standardObj, customerInfo.data.id);
      if (res.success) {
        window.location.assign(res.result);
      }
    }
    if (tier === 'pro') {
      const res = await PaymentsAPI.createCheckoutSession(proObj, customerInfo.data.id);
      if (res.success) {
        window.location.assign(res.result);
      }
    }
    if (tier === 'enterprise') {
      const res = await PaymentsAPI.createCheckoutSession(entObj, customerInfo.data.id);
      if (res.success) {
        window.location.assign(res.result);
      }
    }
  };

  useEffect(() => {
    getStripeCustomer(curTeam?.attributes?.team_admin[0]);
  }, [ curTeam ]);

  useEffect(() => {
    if (!stripeUser.isLoading) {
      getMatchedCusId();
      getAdminUser();
    }
  }, [ stripeUser ]);

  const handleSelectedPlan = (type: string) => {
    switch(type) {
    case 'standard':
      setSelectedPlan('standard');
      break;
    case 'pro':
      setSelectedPlan('pro');
      break;
    case 'enterprise':
      setSelectedPlan('enterprise');
      break;
    default:
      setSelectedPlan('');
    }
  };

  const convertTimestamp = (time: any) => {
    let date: any = new Date(time * 1000);
    date = `${ date.getMonth() + 1 }/${date.getDate() }/${ date.getFullYear()}`;
    return date;
  };

  return (
    <div className='super-admin-edit-access'>
      <Typography className='text-white' variant='h6' component='h2'>
        Start Custom Subscription Here
      </Typography>
      <Typography className='text-white' variant='h6' component='h2'>
        Team Admin: {curTeam?.attributes?.team_admin[0]}
      </Typography>
      <div className='super-admin-access-step'>
        1. Verify that the above admin you are creating the team for is stored in keycloak.
        Otherwise this page will not load info correctly.
      </div>
      <div className='super-admin-access-step'>
        2. Below you can select a plan type and start a trial for this team with a custom amount of days.
        You can adjust quantity in the checkout screen.
      </div>
      <div className='super-admin-access-step'>
        3. All additional team members will need to be created or added to this team via the keycloak admin console.
      </div>
      <div className='super-admin-access-step'>
        4. Any further adjustments to a subscription will need to be done via the Stripe dashboard
      </div>
      <div className='super-admin-access-step'>
        IMPORTANT NOTE: If this customer adds a payment method before their trial is over,
        they will be charged automatically at the end of the free trial period to continue with their subscription.
        Otherwise without a payment method, it will automatically cancel.
      </div>
      {customerInfo?.data?.subscriptions?.data[0] ? (
        <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
          <Typography className='text-white' variant='h6' component='h2'>
            This team already has an active subscription
          </Typography>
          <div className='super-admin-access-step'>
            Plan: {customerInfo?.data?.subscriptions?.data[0]?.metadata?.moduleName}
          </div>
          {customerInfo?.data?.subscriptions?.data[0]?.status === 'trialing' && (
            <div className='super-admin-access-step'>
              Free Trial ends on: {convertTimestamp(customerInfo?.data?.subscriptions?.data[0]?.trial_end)}
            </div>
          )}
          <div className='super-admin-access-step'>
            Number of licenses: {customerInfo?.data?.subscriptions?.data[0]?.quantity}
          </div>
        </div>
      ) : (
        <>
          <div className={`super-admin-plan${selectedPlan === 'standard' ? '-selected' : ''}`} onClick={() => handleSelectedPlan('standard')}>
            <div className='plans-card-header'>
              <div className='plans-card-upper-group'>
                <div className='plans-card-heading-section'>
                  <div className='plans-card-heading-icon'>
                    <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
                      <path d='M12.3578 7.17846C12.2266 7.11287 12.161 7.08008 12.0922 7.06717C12.0313 7.05574 11.9687 7.05574 11.9078 7.06717C11.839 7.08008 11.7734 7.11287 11.6422 7.17846L2 11.9996L11.6422 16.8207C11.7734 16.8863 11.839 16.9191 11.9078 16.932C11.9687 16.9434 12.0313 16.9434 12.0922 16.932C12.161 16.9191 12.2266 16.8863 12.3578 16.8207L22 11.9996L12.3578 7.17846Z' stroke='#4895EF' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
                    </svg>
                  </div>
                  <div className='plans-card-type-text'>
                    BITE Standard plan
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={`super-admin-plan${selectedPlan === 'pro' ? '-selected' : ''}`} onClick={() => handleSelectedPlan('pro')}>
            <div className='plans-card-header'>
              <div className='plans-card-upper-group'>
                <div className='plans-card-heading-section'>
                  <div className='plans-card-heading-icon'>
                    <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
                      <path d='M7 11.9996L2 14.4996L11.6422 19.3207C11.7734 19.3863 11.839 19.4191 11.9078 19.432C11.9687 19.4434 12.0313 19.4434 12.0922 19.432C12.161 19.4191 12.2266 19.3863 12.3578 19.3207L22 14.4996L17 11.9996M2 9.49958L11.6422 4.67846C11.7734 4.61287 11.839 4.58008 11.9078 4.56717C11.9687 4.55574 12.0313 4.55574 12.0922 4.56717C12.161 4.58008 12.2266 4.61287 12.3578 4.67846L22 9.49958L12.3578 14.3207C12.2266 14.3863 12.161 14.4191 12.0922 14.432C12.0313 14.4434 11.9687 14.4434 11.9078 14.432C11.839 14.4191 11.7734 14.3863 11.6422 14.3207L2 9.49958Z' stroke='#4895EF' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
                    </svg>
                  </div>
                  <div className='plans-card-type-text'>
                    BITE Pro plan
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={`super-admin-plan${selectedPlan === 'enterprise' ? '-selected' : ''}`} onClick={() => handleSelectedPlan('enterprise')}>
            <div className='plans-card-header'>
              <div className='plans-card-upper-group'>
                <div className='plans-card-heading-section'>
                  <div className='plans-card-heading-icon'>
                    <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
                      <path d='M2 11.9996L11.6422 16.8207C11.7734 16.8863 11.839 16.9191 11.9078 16.932C11.9687 16.9434 12.0313 16.9434 12.0922 16.932C12.161 16.9191 12.2266 16.8863 12.3578 16.8207L22 11.9996M2 16.9996L11.6422 21.8207C11.7734 21.8863 11.839 21.9191 11.9078 21.932C11.9687 21.9434 12.0313 21.9434 12.0922 21.932C12.161 21.9191 12.2266 21.8863 12.3578 21.8207L22 16.9996M2 6.99958L11.6422 2.17846C11.7734 2.11287 11.839 2.08008 11.9078 2.06717C11.9687 2.05574 12.0313 2.05574 12.0922 2.06717C12.161 2.08008 12.2266 2.11287 12.3578 2.17846L22 6.99958L12.3578 11.8207C12.2266 11.8863 12.161 11.9191 12.0922 11.932C12.0313 11.9434 11.9687 11.9434 11.9078 11.932C11.839 11.9191 11.7734 11.8863 11.6422 11.8207L2 6.99958Z' stroke='#4895EF' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
                    </svg>
                  </div>
                  <div className='plans-card-type-text'>
                    BITE Enterprise plan
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='free-trial-days-input'>
            <div style={{ color: 'white', fontWeight: '600' }}>
              Free Trial Duration (days)
            </div>
            <input
              id='searchVal'
              placeholder='Enter number of days free trial should last'
              type='text'
              className='wl-search-input'
              name='freeTrialDays'
              value={formValues.freeTrialDays}
              onChange={(e) => setFormValues({
                ...formValues,
                freeTrialDays: e.target.value,
              })}
            />
            <div style={{ color: 'white', fontWeight: '600' }}>
              Quantity
            </div>
            <input
              id='searchVal'
              placeholder='Enter quantity of licenses'
              type='text'
              className='wl-search-input'
              name='quantity'
              value={formValues.quantity}
              onChange={(e) => setFormValues({
                ...formValues,
                quantity: e.target.value,
              })}
            />
          </div>
          <Button
            variant='contained'
            className='Theme-Btn'
            type='submit'
            disabled={selectedPlan === ''}
            onClick={() => createCheckoutSession(selectedPlan)}
          >
            Submit
          </Button>
        </>
      )}
    </div>
  );
};

export default EditAccess;
